import { useFetcher } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { Card, Link } from '../primitives'
import Checkbox, { CheckboxGroup } from '~/design-system/primitives/CheckBox'
import { CASHBACK_RULE_URL } from '../helpers/utilsValidation'

interface Consent {
  key: string
  value: boolean
  editable?: boolean
  title?: string
}

interface Profile {
  consents: Consent[]
}

interface ResponsePersonalData {
  response: Profile
  success: boolean
}

interface PrivacyConsentGroupProps {
  disableSwiftEvoceComunicacao?: boolean
  disableSwift_vc?: boolean
  disablePolicyCheckbox?: boolean
  value?: string[]
  onChange?: (values: string[]) => void
  useLoaderUser?: boolean
  isSubmitting?: boolean
  isCorporate?: boolean
}

export default function PrivacyConsentGroup({
  disableSwiftEvoceComunicacao = false,
  disableSwift_vc = false,
  disablePolicyCheckbox = false,
  isSubmitting = false,
  value,
  onChange,
  useLoaderUser = true,
  isCorporate
}: PrivacyConsentGroupProps) {
  const fetcher = useFetcher<ResponsePersonalData>()
  const [user, setUser] = useState<Profile | null>(null)
  const [internalValues, setInternalValues] = useState<string[]>([])
  const values = value ?? internalValues
  const handleChange = onChange ?? setInternalValues

  useEffect(() => {
    if (useLoaderUser) {
      fetcher.submit({ actionType: 'getPersonalData' }, { method: 'post', action: '/myaccount' })
    }
  }, [useLoaderUser])

  useEffect(() => {
    if (fetcher.data?.response) {
      setUser(fetcher.data.response)
    }
  }, [fetcher.data])

  useEffect(() => {
    if (Array.isArray(user?.consents) && !value) {
      const policy = user.consents.find((c) => c.key === 'politica_privacidade')
      const acceptedPolicy = policy?.value === true

      if (!acceptedPolicy) {
        setInternalValues(['swift_vc', 'swiftEvoceComunicacao'])
      } else {
        const accepted = user.consents.filter((c) => c.value).map((c) => c.key)
        setInternalValues(accepted)
      }
    }
  }, [user, value])

  const isPolicyAccepted = user?.consents?.some((c) => c.key === 'politica_privacidade' && c.value === true)
  const shouldDisablePolicy = disablePolicyCheckbox || isSubmitting || isPolicyAccepted

  if (useLoaderUser && !user) return null

  return (
    <div className="flex flex-col gap-0">
      <Card color="secondary">
        <span>
          Você como titular do dado, nos autoriza seu nome completo; CPF; endereço de e-mail; Número de
          telefone, endereço postal; data de nascimento; informações de pagamento; temas de interesse;
          pessoais e de consumo entre outros dados requisitados para realizar as seguintes finalidades:
        </span>
      </Card>

      <div className="inline-flex text-sm">
        <CheckboxGroup
          className="flex flex-col gap-3"
          name="consentimento"
          value={values}
          isRequired
          onChange={handleChange}
        >
          <div className="flex flex-nowrap">
            <Checkbox
              className="items-start mr-1"
              isDisabled={shouldDisablePolicy}
              value="politica_privacidade"
              isRequired
            ></Checkbox>
            Li e aceito a{' '}
            <Link
              className="ml-1"
              href="https://www.swift.com.br/Institucional/politica-privacidade"
              target="_blank"
            >
              Política de Privacidade
            </Link>
          </div>

          {!isCorporate && (
            <div className="flex flex-nowrap">
              <Checkbox
                className="mr-1 items-start text-sm"
                isDisabled={true}
                value="swift_vc"
              ></Checkbox>
              Aceito participar do programa{' '}
              <Link className="ml-1" href={CASHBACK_RULE_URL} target="_blank">
                Sócio Swift
              </Link>
            </div>
          )}

          <div className="flex flex-nowrap">
            <Checkbox
              className="mr-1 items-start"
              isDisabled={disableSwiftEvoceComunicacao}
              value="swiftEvoceComunicacao"
            ></Checkbox>
            <span>Quero receber comunicações da Swift</span>
          </div>
        </CheckboxGroup>
      </div>
    </div>
  )
}
